/**
 * 设备记录面板组件
 */
import React, { useState, useEffect } from 'react';
import { Table, Spin, Empty, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EquipmentUsageService } from '../../../services/equipmentUsageService';
import { formatMoney } from '../../../utils/budgetUtils';

interface EquipmentPanelProps {
  projectId: string;
  isMobile?: boolean;
  active?: boolean;
}

interface EquipmentRecord {
  _id: string;
  usageDate: string;
  equipmentName: string;
  usageHours: number;
  cost: number;
  remarks?: string;
}

const EquipmentPanel: React.FC<EquipmentPanelProps> = ({
  projectId,
  isMobile = false,
  active = false
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<EquipmentRecord[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: isMobile ? 5 : 10,
    total: 0
  });
  const [loaded, setLoaded] = useState(false);

  const fetchData = async (page = 1, pageSize = isMobile ? 5 : 10) => {
    if (!projectId) return;

    setLoading(true);
    try {
      const response = await EquipmentUsageService.getEquipmentUsages({
        projectId,
        page,
        limit: pageSize
      });

      if (response.data) {
        setData(response.data);
        setPagination({
          current: response.pagination?.page || page,
          pageSize: response.pagination?.limit || pageSize,
          total: response.pagination?.total || 0
        });
      }
    } catch (error) {
      console.error('获取设备记录失败:', error);
      message.error('获取设备记录失败');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (active && !loaded) {
      fetchData();
    }
  }, [active, loaded, projectId]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return dateStr.split('T')[0];
  };

  const columns: ColumnsType<EquipmentRecord> = isMobile ? [
    {
      title: '设备/日期',
      key: 'info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.equipmentName}</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {formatDate(record.usageDate)} · {record.usageHours}小时
          </div>
          {record.remarks && (
            <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
              {record.remarks}
            </div>
          )}
        </div>
      )
    },
    {
      title: '金额',
      dataIndex: 'cost',
      key: 'cost',
      width: 100,
      align: 'right',
      render: (val: number) => formatMoney(val || 0, false)
    }
  ] : [
    {
      title: '日期',
      dataIndex: 'usageDate',
      key: 'usageDate',
      width: 120,
      render: (val: string) => formatDate(val)
    },
    {
      title: '设备名称',
      dataIndex: 'equipmentName',
      key: 'equipmentName',
      width: 180,
      ellipsis: true
    },
    {
      title: '使用时长',
      dataIndex: 'usageHours',
      key: 'usageHours',
      width: 120,
      render: (val: number) => `${val}小时`
    },
    {
      title: '金额',
      dataIndex: 'cost',
      key: 'cost',
      width: 130,
      align: 'right',
      render: (val: number) => formatMoney(val || 0, false)
    },
    {
      title: '备注',
      dataIndex: 'remarks',
      key: 'remarks',
      ellipsis: true,
      render: (val: string) => val || '-'
    }
  ];

  if (!loaded && !active) {
    return null;
  }

  if (loading && !data.length) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Spin tip="加载中..." />
      </div>
    );
  }

  if (!data.length) {
    return <Empty description="暂无设备记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey="_id"
      loading={loading}
      size="small"
      pagination={{
        ...pagination,
        size: 'small',
        showSizeChanger: false,
        showTotal: (total) => `共 ${total} 条`,
        onChange: (page, pageSize) => fetchData(page, pageSize)
      }}
    />
  );
};

export default EquipmentPanel;
